import {
    useCallback,
    useState,
} from 'react';

import API from '../../../api/axios';

import {
    mapShopRows,
} from '../utils/discountMappers';


export default function useAssignShops({
    isGlobalAdmin,
    currentCompanyID,
}) {
    const [
        loadingOptions,
        setLoadingOptions,
    ] = useState(false);

    const [
        showAssignModal,
        setShowAssignModal,
    ] = useState(false);

    const [
        assignmentMode,
        setAssignmentMode,
    ] = useState('');

    const [
        selectedSupplier,
        setSelectedSupplier,
    ] = useState('');

    const [
        selectedLocation,
        setSelectedLocation,
    ] = useState('');

    const [
        supplierOptions,
        setSupplierOptions,
    ] = useState([]);

    const [
        globalLocationOptions,
        setGlobalLocationOptions,
    ] = useState([]);

    const [
        scopedLocationOptions,
        setScopedLocationOptions,
    ] = useState([]);

    const [
        existingShopIds,
        setExistingShopIds,
    ] = useState(
        new Set(),
    );

    const [
        modalShops,
        setModalShops,
    ] = useState([]);

    const [
        modalSelection,
        setModalSelection,
    ] = useState({});

    const [
        selectAll,
        setSelectAll,
    ] = useState(false);


    const buildSelectionFromExisting =
        useCallback(
            (
                list,
                pickedSet,
            ) => {
                const sel = {};

                let all =
                    list.length > 0;

                list.forEach(
                    (shop) => {
                        const checked =
                            pickedSet.has(
                                shop.id,
                            );

                        sel[shop.id] =
                            checked;

                        if (!checked) {
                            all = false;
                        }
                    },
                );

                return {
                    sel,
                    all,
                };
            },
            [],
        );


    const resetModal =
        useCallback(() => {
            setAssignmentMode('');
            setSelectedSupplier('');
            setSelectedLocation('');

            setSupplierOptions([]);
            setGlobalLocationOptions([]);
            setScopedLocationOptions([]);

            setModalShops([]);
            setModalSelection({});
            setSelectAll(false);
        }, []);


    const fetchSuppliers =
        useCallback(
            async () => {
                const {
                    data,
                } =
                    await API.get(
                        '/supplier/all-names',
                    );

                return Array.isArray(
                    data,
                )
                    ? data
                    : data?.data || [];
            },
            [],
        );


    const fetchGlobalLocations =
        useCallback(
            async () => {
                const {
                    data,
                } =
                    await API.get(
                        '/location/all-with-path',
                    );

                return Array.isArray(
                    data,
                )
                    ? data
                    : data?.data || [];
            },
            [],
        );


    const fetchScopedLocations =
        useCallback(
            async (
                supplierId,
            ) => {
                const {
                    data,
                } =
                    await API.get(
                        '/location/available',
                        {
                            params: {
                                companyID:
                                    supplierId,
                            },
                        },
                    );

                return Array.isArray(
                    data,
                )
                    ? data
                    : data?.data || [];
            },
            [],
        );


    const fetchShopsBySupplier =
        useCallback(
            async (
                supplierId,
            ) => {
                const {
                    data,
                } =
                    await API.get(
                        `/shop/by-supplier/${supplierId}`,
                    );

                return mapShopRows(
                    data,
                );
            },
            [],
        );


    const fetchShopsByLocation =
        useCallback(
            async (
                locationId,
            ) => {
                const {
                    data,
                } =
                    await API.get(
                        `/shop/by-location/${locationId}`,
                    );

                return mapShopRows(
                    data,
                );
            },
            [],
        );


    /**
     * Scoped company 模式：
     *
     * 根据具体 company +
     * location 查询 shops。
     */
    const fetchShopsByLocationSupplier =
        useCallback(
            async (
                locationId,
                supplierId,
            ) => {
                const {
                    data,
                } =
                    await API.get(
                        `/shop/by-location/${locationId}`,
                        {
                            params: {
                                companyID:
                                    supplierId,
                            },
                        },
                    );

                return mapShopRows(
                    data,
                );
            },
            [],
        );


    const applyShopList =
        useCallback(
            (
                shops,
                existingIds,
            ) => {
                setModalShops(
                    shops,
                );

                const {
                    sel,
                    all,
                } =
                    buildSelectionFromExisting(
                        shops,
                        existingIds,
                    );

                setModalSelection(
                    sel,
                );

                setSelectAll(
                    all,
                );
            },
            [
                buildSelectionFromExisting,
            ],
        );


    const openAssignModal =
        useCallback(
            async (
                formikShops = [],
            ) => {
                const ids =
                    new Set(
                        (
                            formikShops ||
                            []
                        ).map(
                            (item) =>
                                item.shop_id,
                        ),
                    );

                setExistingShopIds(
                    ids,
                );

                setShowAssignModal(
                    true,
                );

                resetModal();

                /**
                 * Scoped company：
                 * 只加载当前 company
                 * 可用 locations。
                 */
                if (!isGlobalAdmin) {
                    if (
                        !currentCompanyID
                    ) {
                        return;
                    }

                    setLoadingOptions(
                        true,
                    );

                    try {
                        const options =
                            await fetchScopedLocations(
                                currentCompanyID,
                            );

                        setScopedLocationOptions([
                            {
                                value:
                                    'total',
                                label:
                                    'Total',
                            },

                            ...options,
                        ]);
                    } finally {
                        setLoadingOptions(
                            false,
                        );
                    }
                }
            },
            [
                isGlobalAdmin,
                currentCompanyID,
                resetModal,
                fetchScopedLocations,
            ],
        );


    const closeAssignModal =
        useCallback(() => {
            setShowAssignModal(
                false,
            );

            resetModal();
        }, [
            resetModal,
        ]);


    const handleAssignmentModeChange =
        useCallback(
            async (
                mode,
            ) => {
                setAssignmentMode(
                    mode,
                );

                setSelectedSupplier('');
                setSelectedLocation('');

                setModalShops([]);
                setModalSelection({});
                setSelectAll(false);

                setSupplierOptions([]);
                setGlobalLocationOptions([]);

                if (!mode) {
                    return;
                }

                setLoadingOptions(
                    true,
                );

                try {
                    if (
                        mode ===
                        'supplier'
                    ) {
                        const suppliers =
                            await fetchSuppliers();

                        setSupplierOptions(
                            suppliers,
                        );
                    }

                    if (
                        mode ===
                        'city'
                    ) {
                        const locations =
                            await fetchGlobalLocations();

                        setGlobalLocationOptions(
                            locations,
                        );
                    }
                } finally {
                    setLoadingOptions(
                        false,
                    );
                }
            },
            [
                fetchSuppliers,
                fetchGlobalLocations,
            ],
        );


    const handleSupplierChange =
        useCallback(
            async (
                supplierId,
            ) => {
                setSelectedSupplier(
                    supplierId,
                );

                setModalShops([]);
                setModalSelection({});
                setSelectAll(false);

                if (!supplierId) {
                    return;
                }

                setLoadingOptions(
                    true,
                );

                try {
                    const shops =
                        await fetchShopsBySupplier(
                            supplierId,
                        );

                    applyShopList(
                        shops,
                        existingShopIds,
                    );
                } finally {
                    setLoadingOptions(
                        false,
                    );
                }
            },
            [
                applyShopList,
                existingShopIds,
                fetchShopsBySupplier,
            ],
        );


    const handleLocationChange =
        useCallback(
            async (
                locationId,
            ) => {
                setSelectedLocation(
                    locationId,
                );

                setModalShops([]);
                setModalSelection({});
                setSelectAll(false);

                if (!locationId) {
                    return;
                }

                setLoadingOptions(
                    true,
                );

                try {
                    let shops = [];

                    /**
                     * Scoped：
                     * Total =
                     * 当前 company 的全部 shops。
                     */
                    if (
                        !isGlobalAdmin &&
                        locationId ===
                        'total'
                    ) {
                        if (
                            currentCompanyID
                        ) {
                            shops =
                                await fetchShopsBySupplier(
                                    currentCompanyID,
                                );
                        }
                    }

                    /**
                     * Scoped：
                     * 当前 company +
                     * location。
                     */
                    else if (
                        !isGlobalAdmin
                    ) {
                        if (
                            currentCompanyID
                        ) {
                            shops =
                                await fetchShopsByLocationSupplier(
                                    locationId,
                                    currentCompanyID,
                                );
                        }
                    }

                    /**
                     * Global admin：
                     * location 下所有公司。
                     */
                    else {
                        shops =
                            await fetchShopsByLocation(
                                locationId,
                            );
                    }

                    applyShopList(
                        shops,
                        existingShopIds,
                    );
                } finally {
                    setLoadingOptions(
                        false,
                    );
                }
            },
            [
                applyShopList,
                existingShopIds,
                fetchShopsBySupplier,
                fetchShopsByLocation,
                fetchShopsByLocationSupplier,
                isGlobalAdmin,
                currentCompanyID,
            ],
        );


    const handleToggleSelectAll =
        useCallback(
            (
                checked,
            ) => {
                setSelectAll(
                    checked,
                );

                const next = {};

                modalShops.forEach(
                    (shop) => {
                        next[shop.id] =
                            checked;
                    },
                );

                setModalSelection(
                    next,
                );
            },
            [
                modalShops,
            ],
        );


    const handleToggleOne =
        useCallback(
            (
                shopId,
                checked,
            ) => {
                setModalSelection(
                    (
                        previous,
                    ) => {
                        const next = {
                            ...previous,
                            [shopId]:
                                checked,
                        };

                        const all =
                            modalShops.length >
                            0 &&
                            modalShops.every(
                                (
                                    shop,
                                ) =>
                                    next[
                                    shop.id
                                    ],
                            );

                        setSelectAll(
                            all,
                        );

                        return next;
                    },
                );
            },
            [
                modalShops,
            ],
        );


    return {
        loadingOptions,
        showAssignModal,

        assignmentMode,
        selectedSupplier,
        selectedLocation,

        supplierOptions,
        globalLocationOptions,
        scopedLocationOptions,

        modalShops,
        modalSelection,
        selectAll,

        openAssignModal,
        closeAssignModal,

        handleAssignmentModeChange,
        handleSupplierChange,
        handleLocationChange,

        handleToggleSelectAll,
        handleToggleOne,
    };
}